import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import clsx from 'clsx';
import ExerciseManagementForm from '../components/ExerciseManagementForm';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 50px)',
    marginTop: '50px',
    padding: theme.spacing(3),
    backgroundColor: '#212121',
    color: 'white',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

const ExerciseFormView = ({ open }) => {
  const classes = useStyles();
  const history = useHistory();
  const { exerciseId } = useParams();

  const [creationMode, setCreationMode] = useState(null);
  const [exercise, setExercise] = useState({});
  const [allExercises, setAllExercises] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);

  const levels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'expert', label: 'Expert' },
    { value: 'athlete', label: 'Athlete' },
    { value: 'all_levels', label: 'All Levels' },
  ];

  const muscles = [
    { value: 'abs', label: 'Abs' },
    { value: 'arms', label: 'Arms' },
    { value: 'back', label: 'Back' },
    { value: 'chest', label: 'Chest' },
    { value: 'legs', label: 'Legs' },
    { value: 'shoulders', label: 'Shoulders' },
    { value: 'multi_area', label: 'Multi Area' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const exercisesQuery = `
        query Exercises {
          exercises {
            id
            name
            description
            level
            muscle_area
            is_public
            use_weights
            equipment_ids
          }
        }
      `;
      const equipmentsQuery = `
        query Equipments {
          equipments {
            id
            name
            type
          }
        }
      `;

      try {
        const [exercisesResponse, equipmentsResponse] = await Promise.all([
          fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: exercisesQuery }),
          }),
          fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: equipmentsQuery }),
          }),
        ]);

        const exercisesResult = await exercisesResponse.json();
        const equipmentsResult = await equipmentsResponse.json();

        if (exercisesResult.data) {
          setAllExercises(exercisesResult.data.exercises);
        }
        if (equipmentsResult.data) {
          setEquipments(equipmentsResult.data.equipments);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleExerciseSelect = exerciseId => {
    const selected = allExercises?.find(ex => ex.id === exerciseId);
    if (selected) {
      setExercise(selected);
      setCreationMode('scratch');
    }
  };

  const handleSubmit = async data => {
    try {
      const exerciseInput = {
        name: data.name,
        description: data.description || '',
        level: exercise.level,
        muscle_area: exercise.muscle_area,
        is_public: data?.is_public === true || data?.is_public === 'true',
        use_weights: data?.use_weights === true || data?.use_weights === 'true',
        equip_settings: exercise.equipment_ids ? { equipment_ids: exercise.equipment_ids } : null,
        archive: false,
      };

      const query = `
          mutation CreateExercise($input: ExerciseInput!) {
            createExercise(input: $input) {
              id
            }
          }
        `;
      const variables = {
        input: exerciseInput,
      };

      const response = await fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      const result = await response.json();

      if (result.errors) {
        alert(`Error creating exercise. Please try again.`);
      } else {
        history.push('/guryou-ai-studio/exercises');
      }
    } catch (error) {
      alert(`Error creating exercise. Please try again.`);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <main
      className={clsx(classes.content, {
        [classes.contentShift]: open,
      })}
    >
      <Box style={{ overflowY: 'auto', flex: 1 }}>
        <IconButton onClick={() => history.goBack()} style={{ marginBottom: '16px' }}>
          <ChevronLeftIcon style={{ color: 'white' }} />
        </IconButton>
        <ExerciseManagementForm
          handleSubmit={handleSubmit}
          exercise={exercise}
          setExercise={setExercise}
          allExercises={allExercises}
          handleExerciseSelect={handleExerciseSelect}
          creationMode={creationMode}
          setCreationMode={setCreationMode}
          exerciseId={exerciseId}
          levels={levels}
          muscles={muscles}
          equipments={equipments}
        />
      </Box>
    </main>
  );
};

export default ExerciseFormView;
